/** Max dimension sent to vision API (pixels on longest edge). */
const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.82;
/** Skip re-encoding if file is already small enough. */
const SKIP_IF_UNDER_BYTES = 450 * 1024;

/**
 * Resize and re-encode phone photos before upload to cut memory on Heroku and speed vision calls.
 * Returns a JPEG File unless the source is already small.
 */
export async function compressImageForVision(file) {
	if (!file?.type?.startsWith('image/')) {
		return file;
	}

	if (file.size <= SKIP_IF_UNDER_BYTES && file.type === 'image/jpeg') {
		return file;
	}

	const dataUrl = await readFileAsDataUrl(file);
	const img = await loadImage(dataUrl);

	const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
	const width = Math.max(1, Math.round(img.width * scale));
	const height = Math.max(1, Math.round(img.height * scale));

	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext('2d');
	if (!ctx) {
		return file;
	}

	ctx.drawImage(img, 0, 0, width, height);

	const blob = await canvasToJpegBlob(canvas, JPEG_QUALITY);
	const baseName = (file.name || 'food-photo').replace(/\.[^.]+$/, '');
	return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

function readFileAsDataUrl(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = () => reject(new Error('Could not read image.'));
		reader.readAsDataURL(file);
	});
}

function loadImage(src) {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error('Could not load image.'));
		img.src = src;
	});
}

function canvasToJpegBlob(canvas, quality) {
	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (blob) {
					resolve(blob);
				} else {
					reject(new Error('Could not compress image.'));
				}
			},
			'image/jpeg',
			quality
		);
	});
}
